/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  defaultActivitiesEventsFilterValues,
  defaultListingPageSize,
} from "Constants/ApplicationConstants";
import { OperationStatus } from "Constants/OperationStatus";

import {
  IActivitiesEventsFilterValueAdd,
  IActivitiesEventsFilterValueBulkRemove,
  IActivitiesEventsFilterValueRemove,
  IActivitiesEventsFilterValueReset,
  IGetAdditionalActivitiesEventsBySelectedUserprofileIdFail,
  IGetAdditionalActivitiesEventsBySelectedUserprofileIdSuccess,
  IGetApplicationFlagsBySelectedApplicationIdFail,
  IGetApplicationFlagsBySelectedApplicationIdSuccess,
  IGetApplicationQuestionsBySelectedApplicationIdFail,
  IGetApplicationQuestionsBySelectedApplicationIdSuccess,
  IGetHighlightedActivitiesEventsBySelectedApplicationIdFail,
  IGetHighlightedActivitiesEventsBySelectedApplicationIdSuccess,
  IGetUserProfileBySelectedApplicationIdFail,
  IGetUserProfileBySelectedApplicationIdSuccess,
  IUpdateApplicationStatusesAndRemoveFlagsFail,
  IUpdateApplicationTechnologyAreaAndResetScoresFail,
} from "./ApplicationDetailsAction";
import {
  ACTIVITIES_EVENTS_FILTER_VALUE_ADD,
  ACTIVITIES_EVENTS_FILTER_VALUE_BULK_REMOVE,
  ACTIVITIES_EVENTS_FILTER_VALUE_REMOVE,
  ACTIVITIES_EVENTS_FILTER_VALUE_RESET,
  CLEAR_APPLICATION_DETAILS_STATE,
  GET_ACTIVITY_EVENT_BY_ID,
  GET_ACTIVITY_EVENT_BY_ID_FAIL,
  GET_ACTIVITY_EVENT_BY_ID_SUCCESS,
  GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID,
  GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_FAIL,
  GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_SUCCESS,
  GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID,
  GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_SUCCESS,
  GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID,
  GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_SUCCESS,
  GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID,
  GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_SUCCESS,
  GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID,
  GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_FAIL,
  GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_SUCCESS,
  UPDATE_ACTIVITIES_EVENTS_CURRENT_PAGE,
  UPDATE_ACTIVITIES_EVENTS_PAGE_SIZE,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_FAIL,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_RESET,
  UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_SUCCESS,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_FAIL,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_RESET,
  UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_SUCCESS,
} from "./ApplicationDetailsActionTypes";
import { IApplicationDetailsState } from "./IApplicationDetailsState";

export const initialState: IApplicationDetailsState = {
  additionalActivitiesEventsState: {
    activitiesEventsFilterValues: defaultActivitiesEventsFilterValues,
    filteredCount: 0,
    currentPage: 1,
    pageSize: defaultListingPageSize,
    activitiesEvents: [],
  },
  highlightedActivitiesEventsState: {
    activitiesEventsFilterValues: defaultActivitiesEventsFilterValues,
    filteredCount: 0,
    currentPage: 1,
    pageSize: defaultListingPageSize,
    activitiesEvents: [],
  },
};

const applyFilterValueAdd = (
  state: IApplicationDetailsState,
  action: IActivitiesEventsFilterValueAdd
): IApplicationDetailsState => {
  if (action.isHighlighted) {
    return {
      ...state,
      highlightedActivitiesEventsState: {
        ...state.highlightedActivitiesEventsState,
        activitiesEventsFilterValues: [
          ...state.highlightedActivitiesEventsState.activitiesEventsFilterValues,
          action.filterValue,
        ],
      },
    };
  } else {
    return {
      ...state,
      additionalActivitiesEventsState: {
        ...state.additionalActivitiesEventsState,
        activitiesEventsFilterValues: [
          ...state.additionalActivitiesEventsState.activitiesEventsFilterValues,
          action.filterValue,
        ],
      },
    };
  }
};

const applyFilterValueRemove = (
  state: IApplicationDetailsState,
  action: IActivitiesEventsFilterValueRemove
): IApplicationDetailsState => {
  if (action.isHighlighted) {
    return {
      ...state,
      highlightedActivitiesEventsState: {
        ...state.highlightedActivitiesEventsState,
        activitiesEventsFilterValues:
          state.highlightedActivitiesEventsState.activitiesEventsFilterValues.filter(
            (fv) => fv.value !== action.filterValue.value
          ),
      },
    };
  } else {
    return {
      ...state,
      additionalActivitiesEventsState: {
        ...state.additionalActivitiesEventsState,
        activitiesEventsFilterValues:
          state.additionalActivitiesEventsState.activitiesEventsFilterValues.filter(
            (fv) => fv.value !== action.filterValue.value
          ),
      },
    };
  }
};

const applyFilterValueBulkRemove = (
  state: IApplicationDetailsState,
  action: IActivitiesEventsFilterValueBulkRemove
): IApplicationDetailsState => {
  if (action.isHighlighted) {
    return {
      ...state,
      highlightedActivitiesEventsState: {
        ...state.highlightedActivitiesEventsState,
        activitiesEventsFilterValues:
          state.highlightedActivitiesEventsState.activitiesEventsFilterValues.filter(
            (sfv) => !action.filterValues.some((fv) => fv.value === sfv.value)
          ),
      },
    };
  } else {
    return {
      ...state,
      additionalActivitiesEventsState: {
        ...state.additionalActivitiesEventsState,
        activitiesEventsFilterValues:
          state.additionalActivitiesEventsState.activitiesEventsFilterValues.filter(
            (sfv) => !action.filterValues.some((fv) => fv.value === sfv.value)
          ),
      },
    };
  }
};

const applyFilterValueReset = (
  state: IApplicationDetailsState,
  action: IActivitiesEventsFilterValueReset
): IApplicationDetailsState => {
  if (action.isHighlighted) {
    return {
      ...state,
      highlightedActivitiesEventsState: initialState.highlightedActivitiesEventsState,
    };
  } else {
    return {
      ...state,
      additionalActivitiesEventsState: initialState.additionalActivitiesEventsState,
    };
  }
};

const applyGetUserProfileBySelectedApplicationId = (
  state: IApplicationDetailsState
): IApplicationDetailsState => ({
  ...state,
  selectedUserProfileInformation: undefined,
  selectedUserProfileInformationOpStatus: {
    read: OperationStatus.Pending,
  },
  selectedUserProfileInformationErrorMessage: undefined,
});

const applyGetUserProfileBySelectedApplicationIdSuccess = (
  state: IApplicationDetailsState,
  action: IGetUserProfileBySelectedApplicationIdSuccess
): IApplicationDetailsState => ({
  ...state,
  selectedUserProfileInformation: action.userProfileInfo,
  selectedUserProfileInformationOpStatus: {
    read: OperationStatus.Succeeded,
  },
  selectedUserProfileInformationErrorMessage: undefined,
});

const applyGetUserProfileBySelectedApplicationIdFail = (
  state: IApplicationDetailsState,
  action: IGetUserProfileBySelectedApplicationIdFail
): IApplicationDetailsState => ({
  ...state,

  selectedUserProfileInformation: undefined,
  selectedUserProfileInformationOpStatus: {
    read: OperationStatus.Failed,
  },
  selectedUserProfileInformationErrorMessage: action.errorMessage,
});

const applyGetApplicationFlagsBySelectedApplicationId = (
  state: IApplicationDetailsState
): IApplicationDetailsState => ({
  ...state,
  selectedApplicationFlags: undefined,
  selectedApplicationFlagsOpStatus: {
    read: OperationStatus.Pending,
  },
  selectedApplicationFlagsErrorMessage: undefined,
});

const applyGetApplicationFlagsBySelectedApplicationIdSuccess = (
  state: IApplicationDetailsState,
  action: IGetApplicationFlagsBySelectedApplicationIdSuccess
): IApplicationDetailsState => ({
  ...state,
  selectedApplicationFlags: action.applicationFlags,
  selectedApplicationFlagsOpStatus: {
    read: OperationStatus.Succeeded,
  },
  selectedApplicationFlagsErrorMessage: undefined,
});

const applyGetApplicationFlagsBySelectedApplicationIdFail = (
  state: IApplicationDetailsState,
  action: IGetApplicationFlagsBySelectedApplicationIdFail
): IApplicationDetailsState => ({
  ...state,
  selectedApplicationFlags: undefined,
  selectedApplicationFlagsOpStatus: {
    read: OperationStatus.Failed,
  },
  selectedApplicationFlagsErrorMessage: action.errorMessage,
});

const applyGetApplicationQuestionsBySelectedApplicationId = (
  state: IApplicationDetailsState
): IApplicationDetailsState => ({
  ...state,
  selectedApplicationQuestions: undefined,
  selectedApplicationQuestionsOpStatus: {
    read: OperationStatus.Pending,
  },
  selectedApplicationQuestionsErrorMessage: undefined,
});

const applyGetApplicationQuestionsBySelectedApplicationIdSuccess = (
  state: IApplicationDetailsState,
  action: IGetApplicationQuestionsBySelectedApplicationIdSuccess
): IApplicationDetailsState => ({
  ...state,
  selectedApplicationQuestions: action.applicationQuestions,
  selectedApplicationQuestionsOpStatus: {
    read: OperationStatus.Succeeded,
  },
  selectedApplicationQuestionsErrorMessage: undefined,
});

const applyGetApplicationQuestionsBySelectedApplicationIdFail = (
  state: IApplicationDetailsState,
  action: IGetApplicationQuestionsBySelectedApplicationIdFail
): IApplicationDetailsState => ({
  ...state,
  selectedApplicationQuestions: undefined,
  selectedApplicationQuestionsOpStatus: {
    read: OperationStatus.Failed,
  },
  selectedApplicationQuestionsErrorMessage: action.errorMessage,
});

const applyGetHighlightedActivitiesEventsBySelectedApplicationId = (
  state: IApplicationDetailsState
): IApplicationDetailsState => ({
  ...state,
  highlightedActivitiesEventsState: {
    ...state.highlightedActivitiesEventsState,
    activitiesEvents: undefined,
    filteredCount: 0,
    activitiesEventsOpStatus: {
      read: OperationStatus.Pending,
    },
    activitiesEventsErrorMessage: undefined,
  },
});

const applyGetHighlightedActivitiesEventsBySelectedApplicationIdSuccess = (
  state: IApplicationDetailsState,
  action: IGetHighlightedActivitiesEventsBySelectedApplicationIdSuccess
): IApplicationDetailsState => ({
  ...state,
  highlightedActivitiesEventsState: {
    ...state.highlightedActivitiesEventsState,
    activitiesEvents: action.highlightedActivitiesEvents.activitiesEvents,
    filteredCount: action.highlightedActivitiesEvents.totalCount,
    activitiesEventsOpStatus: {
      read: OperationStatus.Succeeded,
    },
    activitiesEventsErrorMessage: undefined,
  },
});

const applyGetHighlightedActivitiesEventsBySelectedApplicationIdFail = (
  state: IApplicationDetailsState,
  action: IGetHighlightedActivitiesEventsBySelectedApplicationIdFail
): IApplicationDetailsState => ({
  ...state,
  highlightedActivitiesEventsState: {
    ...state.highlightedActivitiesEventsState,
    activitiesEvents: undefined,
    filteredCount: 0,
    activitiesEventsOpStatus: {
      read: OperationStatus.Failed,
    },
    activitiesEventsErrorMessage: action.errorMessage,
  },
});

const applyGetAdditionalActivitiesEventsBySelectedUserprofileId = (
  state: IApplicationDetailsState
): IApplicationDetailsState => ({
  ...state,
  additionalActivitiesEventsState: {
    ...state.additionalActivitiesEventsState,
    activitiesEvents: undefined,
    filteredCount: 0,
    activitiesEventsOpStatus: {
      read: OperationStatus.Pending,
    },
    activitiesEventsErrorMessage: undefined,
  },
});

const applyGetAdditionalActivitiesEventsBySelectedUserprofileIdSuccess = (
  state: IApplicationDetailsState,
  action: IGetAdditionalActivitiesEventsBySelectedUserprofileIdSuccess
): IApplicationDetailsState => ({
  ...state,
  additionalActivitiesEventsState: {
    ...state.additionalActivitiesEventsState,
    activitiesEvents: action.additionalActivitiesEvents.activitiesEvents,
    filteredCount: action.additionalActivitiesEvents.totalCount,
    activitiesEventsOpStatus: {
      read: OperationStatus.Succeeded,
    },
    activitiesEventsErrorMessage: undefined,
  },
});

const applyGetAdditionalActivitiesEventsBySelectedUserprofileIdFail = (
  state: IApplicationDetailsState,
  action: IGetAdditionalActivitiesEventsBySelectedUserprofileIdFail
): IApplicationDetailsState => ({
  ...state,
  additionalActivitiesEventsState: {
    ...state.additionalActivitiesEventsState,
    activitiesEvents: undefined,
    filteredCount: 0,
    activitiesEventsOpStatus: {
      read: OperationStatus.Failed,
    },
    activitiesEventsErrorMessage: action.errorMessage,
  },
});

const applyUpdateActivitiesEventsPageSize = (
  state: IApplicationDetailsState,
  action
): IApplicationDetailsState => {
  if (action.isHighlighted) {
    return {
      ...state,
      highlightedActivitiesEventsState: {
        ...state.highlightedActivitiesEventsState,
        pageSize: action.pageSize,
        currentPage: 1,
      },
    };
  } else {
    return {
      ...state,
      additionalActivitiesEventsState: {
        ...state.additionalActivitiesEventsState,
        pageSize: action.pageSize,
        currentPage: 1,
      },
    };
  }
};

const applyUpdateActivitiesEventsCurrentPage = (
  state: IApplicationDetailsState,
  action
): IApplicationDetailsState => {
  if (action.isHighlighted) {
    return {
      ...state,
      highlightedActivitiesEventsState: {
        ...state.highlightedActivitiesEventsState,
        currentPage: action.currentPage,
      },
    };
  } else {
    return {
      ...state,
      additionalActivitiesEventsState: {
        ...state.additionalActivitiesEventsState,
        currentPage: action.currentPage,
      },
    };
  }
};

const applyUpdateApplicationStatusesAndRemoveFlags = (
  state: IApplicationDetailsState,
  action
): IApplicationDetailsState => {
  return {
    ...state,
    manageApplicationOpStatus: {
      update: OperationStatus.Pending,
    },
    manageApplicationErrorMessage: undefined,
  };
};

const applyUpdateApplicationStatusesAndRemoveFlagsFail = (
  state: IApplicationDetailsState,
  action: IUpdateApplicationStatusesAndRemoveFlagsFail
): IApplicationDetailsState => {
  return {
    ...state,
    manageApplicationOpStatus: {
      update: OperationStatus.Failed,
    },
    manageApplicationErrorMessage: action.errorMessage,
  };
};

const applyUpdateApplicationStatusesAndRemoveFlagsSuccess = (
  state: IApplicationDetailsState,
  action
): IApplicationDetailsState => {
  return {
    ...state,
    manageApplicationOpStatus: {
      update: OperationStatus.Succeeded,
    },
    manageApplicationErrorMessage: undefined,
  };
};

const applyUpdateApplicationStatusesAndRemoveFlagsReset = (
  state: IApplicationDetailsState
): IApplicationDetailsState => {
  return {
    ...state,
    manageApplicationOpStatus: {
      update: OperationStatus.Idle,
    },
    manageApplicationErrorMessage: undefined,
  };
};
const applyUpdateApplicationTechnologyAreaAndResetScores = (
  state: IApplicationDetailsState,
  action
): IApplicationDetailsState => {
  return {
    ...state,
    updateTechnologyAreaOpStatus: {
      update: OperationStatus.Pending,
    },
    updateTechnologyAreaErrorMessage: undefined,
  };
};

const applyUpdateApplicationTechnologyAreaAndResetScoresFail = (
  state: IApplicationDetailsState,
  action: IUpdateApplicationTechnologyAreaAndResetScoresFail
): IApplicationDetailsState => {
  return {
    ...state,
    updateTechnologyAreaOpStatus: {
      update: OperationStatus.Failed,
    },
    updateTechnologyAreaErrorMessage: action.errorMessage,
  };
};

const applyUpdateApplicationTechnologyAreaAndResetScoresSuccess = (
  state: IApplicationDetailsState,
  action
): IApplicationDetailsState => {
  return {
    ...state,
    updateTechnologyAreaOpStatus: {
      update: OperationStatus.Succeeded,
    },
    updateTechnologyAreaErrorMessage: undefined,
  };
};

const applyUpdateApplicationTechnologyAreaAndResetScoresReset = (
  state: IApplicationDetailsState
): IApplicationDetailsState => {
  return {
    ...state,
    updateTechnologyAreaOpStatus: {
      update: OperationStatus.Idle,
    },
    updateTechnologyAreaErrorMessage: undefined,
  };
};

/**
 * Reducer function for activity event types.
 * @param state activity event types state.
 * @param action action.
 * @returns {IApplicationDetailsState} New activity event types state.
 */
function applicationDetailsReducer(
  state: IApplicationDetailsState = initialState,
  action
): IApplicationDetailsState {
  switch (action.type) {
    case ACTIVITIES_EVENTS_FILTER_VALUE_ADD:
      return applyFilterValueAdd(state, action);
    case ACTIVITIES_EVENTS_FILTER_VALUE_REMOVE:
      return applyFilterValueRemove(state, action);
    case ACTIVITIES_EVENTS_FILTER_VALUE_BULK_REMOVE:
      return applyFilterValueBulkRemove(state, action);
    case UPDATE_ACTIVITIES_EVENTS_PAGE_SIZE:
      return applyUpdateActivitiesEventsPageSize(state, action);
    case UPDATE_ACTIVITIES_EVENTS_CURRENT_PAGE:
      return applyUpdateActivitiesEventsCurrentPage(state, action);
    case ACTIVITIES_EVENTS_FILTER_VALUE_RESET:
      return applyFilterValueReset(state, action);
    case GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID: {
      return applyGetUserProfileBySelectedApplicationId(state);
    }
    case GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_SUCCESS: {
      return applyGetUserProfileBySelectedApplicationIdSuccess(state, action);
    }
    case GET_USER_PROFILE_BY_SELECTED_APPLICATION_ID_FAIL: {
      return applyGetUserProfileBySelectedApplicationIdFail(state, action);
    }
    case GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID: {
      return applyGetApplicationFlagsBySelectedApplicationId(state);
    }
    case GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_SUCCESS: {
      return applyGetApplicationFlagsBySelectedApplicationIdSuccess(state, action);
    }
    case GET_APPLICATION_FLAGS_BY_SELECTED_APPLICATION_ID_FAIL: {
      return applyGetApplicationFlagsBySelectedApplicationIdFail(state, action);
    }
    case GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID: {
      return applyGetApplicationQuestionsBySelectedApplicationId(state);
    }
    case GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_SUCCESS: {
      return applyGetApplicationQuestionsBySelectedApplicationIdSuccess(state, action);
    }
    case GET_APPLICATION_QUESTIONS_BY_SELECTED_APPLICATION_ID_FAIL: {
      return applyGetApplicationQuestionsBySelectedApplicationIdFail(state, action);
    }
    case GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID: {
      return applyGetHighlightedActivitiesEventsBySelectedApplicationId(state);
    }
    case GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_SUCCESS: {
      return applyGetHighlightedActivitiesEventsBySelectedApplicationIdSuccess(state, action);
    }
    case GET_HIGHLIGHTED_ACTIVITIES_EVENTS_BY_SELECTED_APPLICATION_ID_FAIL: {
      return applyGetHighlightedActivitiesEventsBySelectedApplicationIdFail(state, action);
    }
    case GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID: {
      return applyGetAdditionalActivitiesEventsBySelectedUserprofileId(state);
    }
    case GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_SUCCESS: {
      return applyGetAdditionalActivitiesEventsBySelectedUserprofileIdSuccess(state, action);
    }
    case GET_ADDITIONAL_ACTIVITIES_EVENTS_BY_SELECTED_USERPROFILE_ID_FAIL: {
      return applyGetAdditionalActivitiesEventsBySelectedUserprofileIdFail(state, action);
    }
    case UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS: {
      return applyUpdateApplicationStatusesAndRemoveFlags(state, action);
    }
    case UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_FAIL: {
      return applyUpdateApplicationStatusesAndRemoveFlagsFail(state, action);
    }
    case UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_SUCCESS: {
      return applyUpdateApplicationStatusesAndRemoveFlagsSuccess(state, action);
    }
    case UPDATE_APPLICATION_STATUSES_AND_REMOVE_FLAGS_RESET: {
      return applyUpdateApplicationStatusesAndRemoveFlagsReset(state);
    }
    case UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES: {
      return applyUpdateApplicationTechnologyAreaAndResetScores(state, action);
    }
    case UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_FAIL: {
      return applyUpdateApplicationTechnologyAreaAndResetScoresFail(state, action);
    }
    case UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_SUCCESS: {
      return applyUpdateApplicationTechnologyAreaAndResetScoresSuccess(state, action);
    }
    case UPDATE_APPLICATION_TECHNOLOGY_AREA_AND_RESET_SCORES_RESET: {
      return applyUpdateApplicationTechnologyAreaAndResetScoresReset(state);
    }
    case GET_ACTIVITY_EVENT_BY_ID_SUCCESS: {
      return {
        ...state,
        selectedActivityEvent: action.activityEvent,
        selectedActivityEventOpStatus: {
          read: OperationStatus.Succeeded,
        },
      };
    }
    case GET_ACTIVITY_EVENT_BY_ID_FAIL: {
      return {
        ...state,
        selectedActivityEvent: undefined,
        selectedActivityEventOpStatus: {
          read: OperationStatus.Failed,
        },
        selectedActivityEventErrorMessage: action.errorMessage,
      };
    }
    case GET_ACTIVITY_EVENT_BY_ID: {
      return {
        ...state,
        selectedActivityEvent: undefined,
        selectedActivityEventOpStatus: {
          read: OperationStatus.Pending,
        },
        selectedActivityEventErrorMessage: undefined,
      };
    }
    case CLEAR_APPLICATION_DETAILS_STATE: {
      return initialState;
    }
    default:
      return state;
  }
}

export default applicationDetailsReducer;
